import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { ReactNode, useEffect } from "react";
import type { ScrollViewProps } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { agendaMemberNotes_atom } from "../../../../../../features/MemberNotes/atoms";
import {
  MultiSlotAgendaEventsQuery,
  MultiSlotAgendaEventsQuery$data,
} from "./__generated__/MultiSlotAgendaEventsQuery.graphql";
import {
  MultiSlotAgendaQuery,
  MultiSlotAgendaQuery$data,
} from "./__generated__/MultiSlotAgendaQuery.graphql";
import { useInitializeAgendaEventNodesByIdAtom } from "./atoms/agendaEventNodesByIdAtom/agendaEventNodesByIdAtom";
import { BidirectionalStickyScroller } from "./components/BidirectionalStickyScroller";

type MultiSlotAgendaProps = {
  eventsQueryRef: PreloadedQuery<MultiSlotAgendaEventsQuery>;
  queryRefs: readonly PreloadedQuery<MultiSlotAgendaQuery>[];
  currentSlotId?: string;
  handleMultiSlotRowPress?: (slotId: string) => void;
  setBookingWizardSlotId?: (slotId: string) => void;
  setBookingWizardStartTime?: (startTime: string) => void;
  onVerticalScroll?: ScrollViewProps["onScroll"];
};

const mobileScreenBreakpointValues = {
  base: 1,
  xs: 1,
  sm: 1,
  md: 1,
  agendaSplit: 0,
  eventCardColumn: 0,
  lg: 0,
  xl: 0,
};

export const MultiSlotAgenda = ({
  eventsQueryRef,
  queryRefs,
  currentSlotId,
  handleMultiSlotRowPress,
  setBookingWizardSlotId,
  setBookingWizardStartTime,
  onVerticalScroll,
}: MultiSlotAgendaProps) => {
  const eventsData = usePreloadedQuery(
    MultiSlotAgendaEventsQueryString,
    eventsQueryRef
  );

  return (
    <MultiSlotAgendaBatchDataCollector queryRefs={queryRefs}>
      {(batchData) => (
        <MultiSlotAgendaContent
          batchData={batchData}
          eventsData={eventsData}
          currentSlotId={currentSlotId}
          handleMultiSlotRowPress={handleMultiSlotRowPress}
          setBookingWizardSlotId={setBookingWizardSlotId}
          setBookingWizardStartTime={setBookingWizardStartTime}
          onVerticalScroll={onVerticalScroll}
        />
      )}
    </MultiSlotAgendaBatchDataCollector>
  );
};

type MultiSlotAgendaContentProps = Omit<
  MultiSlotAgendaProps,
  "eventsQueryRef" | "queryRefs"
> & {
  batchData: readonly MultiSlotAgendaQuery$data[];
  eventsData: MultiSlotAgendaEventsQuery$data;
};

const MultiSlotAgendaContent = ({
  batchData,
  eventsData,
  currentSlotId,
  handleMultiSlotRowPress,
  setBookingWizardSlotId,
  setBookingWizardStartTime,
  onVerticalScroll,
}: MultiSlotAgendaContentProps) => {
  const facilityRefs = batchData.flatMap((data) =>
    data.node?.__typename === "Facility" ? [data.node] : []
  );
  const agendaEventRefs =
    eventsData.node?.__typename === "Facility"
      ? eventsData.node.agendas?.edges?.flatMap((edge) =>
          edge?.node ? [edge.node] : []
        ) ?? []
      : [];

  const [memberNote, setMemberNote] = useAtom(agendaMemberNotes_atom);

  const agenda =
    agendaEventRefs.find((agenda) => agenda.memberNote) ?? agendaEventRefs[0];

  const isMobileScreen = useResponsiveProp(mobileScreenBreakpointValues) === 1;

  useEffect(() => {
    if (agenda && agenda.memberNote) {
      setMemberNote({
        header: agenda.memberNote.header,
        body: agenda.memberNote.body,
        status: agenda.memberNote.status,
        date: agenda.date,
      });
    } else {
      setMemberNote(null);
    }
  }, [agenda, setMemberNote]);

  /**
   * We initialize a map of Event ID to the GQL fragment key necessary for display
   * so that we can access them on every slot -- without ballooning our response
   * payload via recursive GQL queries.
   */
  useInitializeAgendaEventNodesByIdAtom({
    agendaRefs: agendaEventRefs,
  });

  return facilityRefs.length ? (
    <>
      {memberNote && isMobileScreen ? (
        <Box px={4} bg="surface-primary">
          <CourseNote
            header={memberNote.header}
            body={memberNote.body}
            priority={memberNote.status}
          />
        </Box>
      ) : null}
      <HStack flex={1} bg="surface-primary">
        <BidirectionalStickyScroller
          currentSlotId={currentSlotId}
          handleMultiSlotRowPress={handleMultiSlotRowPress}
          facilityRefs={facilityRefs}
          onVerticalScroll={onVerticalScroll}
          setBookingWizardSlotId={setBookingWizardSlotId}
          setBookingWizardStartTime={setBookingWizardStartTime}
        />
      </HStack>
    </>
  ) : null;
};

type MultiSlotAgendaBatchDataCollectorProps = {
  queryRefs: readonly PreloadedQuery<MultiSlotAgendaQuery>[];
  children: (batchData: readonly MultiSlotAgendaQuery$data[]) => ReactNode;
};

const MultiSlotAgendaBatchDataCollector = ({
  queryRefs,
  children,
}: MultiSlotAgendaBatchDataCollectorProps) => {
  const [firstQueryRef, ...remainingQueryRefs] = queryRefs;

  if (!firstQueryRef) {
    return <>{children([])}</>;
  }

  return (
    <MultiSlotAgendaBatchDataReader
      queryRef={firstQueryRef}
      remainingQueryRefs={remainingQueryRefs}
      batchData={[]}
    >
      {children}
    </MultiSlotAgendaBatchDataReader>
  );
};

type MultiSlotAgendaBatchDataReaderProps = {
  queryRef: PreloadedQuery<MultiSlotAgendaQuery>;
  remainingQueryRefs: readonly PreloadedQuery<MultiSlotAgendaQuery>[];
  batchData: readonly MultiSlotAgendaQuery$data[];
  children: (batchData: readonly MultiSlotAgendaQuery$data[]) => ReactNode;
};

const MultiSlotAgendaBatchDataReader = ({
  queryRef,
  remainingQueryRefs,
  batchData,
  children,
}: MultiSlotAgendaBatchDataReaderProps) => {
  const data = usePreloadedQuery(MultiSlotAgendaQueryString, queryRef);
  const nextBatchData = [...batchData, data];
  const [nextQueryRef, ...nextRemainingQueryRefs] = remainingQueryRefs;

  if (!nextQueryRef) {
    return <>{children(nextBatchData)}</>;
  }

  return (
    <MultiSlotAgendaBatchDataReader
      queryRef={nextQueryRef}
      remainingQueryRefs={nextRemainingQueryRefs}
      batchData={nextBatchData}
    >
      {children}
    </MultiSlotAgendaBatchDataReader>
  );
};

export const MultiSlotAgendaQueryString = graphql`
  query MultiSlotAgendaQuery(
    $facilityId: ID!
    $date: Date!
    $courseIds: [ID!]!
    $requestConfigs: Boolean!
  ) {
    node(id: $facilityId) {
      id
      __typename
      ... on Facility {
        id
        slug
        type
        ...BidirectionalStickyScroller_facilities
      }
    }
  }
`;

export const MultiSlotAgendaEventsQueryString = graphql`
  query MultiSlotAgendaEventsQuery(
    $facilityId: ID!
    $date: Date!
    $memberId: ID!
    $requestConfigs: Boolean!
  ) {
    node(id: $facilityId) {
      id
      __typename
      ... on Facility {
        id
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              date
              id
              queueSlot {
                id
              }
              requestRestrictions: bookingRules(type: [REQUEST])
                @skip(if: $requestConfigs) {
                edges {
                  node {
                    id
                  }
                }
              }
              configuredRequestRestrictions: requestWindowBookingRules(
                type: [REQUEST]
              ) @include(if: $requestConfigs) {
                edges {
                  requestOpens
                  requestCloses
                  node {
                    id
                  }
                }
              }
              memberNote {
                id
                header
                body
                status
              }
              ...agendaEventNodesByIdAtom_events
            }
          }
        }
      }
    }
  }
`;
